import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  TextField,
  Typography,
  Card,
  Divider,
  Grid,
  IconButton,
  InputLabel
} from '@mui/material';
import { X, Calendar, Users, CreditCard, Check } from 'lucide-react';
import { KeyUri } from '../key';
import axios from 'axios';
import { fetchUserTerms, termsSelector } from '../api/termsSlice';
import { jwtDecode } from 'jwt-decode';
import { trackEvent } from "../utils/metaPixel";

function getMetaCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function getFbc() {
  let fbc = getMetaCookie("_fbc");
  if (!fbc) {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}`;
    }
  }
  return fbc;
}

export function BookingModal({ property, onClose, selectedAccommodation,  
  checkIn: prefilledCheckIn, 
  checkOut: prefilledCheckOut, 
  guests: prefilledGuests,
 accommodationQuantity: prefilledQuantity = 1, 
  coupon,
discount = 0 ,
 mealPlan = 'EP',           
  mealPlanPrice = null, 
eventId,  }) {
  const navigate = useNavigate(); 
  const termsState = useSelector(termsSelector);
  const { currentTerms } = termsState;

  const [step, setStep] = useState(1);

   const [paymentGateway, setPaymentGateway] = useState('cashfree');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Helper to get lowest priced accommodation
  const getLowestPricedAccommodation = (accommodations) => {
    if (!Array.isArray(accommodations) || accommodations.length === 0) return null;
    return accommodations.reduce((lowest, current) => {
      const lowestPrice = lowest?.pricePerNightB2C ?? Infinity;
      const currentPrice = current?.pricePerNightB2C ?? Infinity;
      return currentPrice < lowestPrice ? current : lowest;
    }, accommodations[0]);
  };

  const [selectedRoom, setSelectedRoom] = useState(() =>
    selectedAccommodation || getLowestPricedAccommodation(property.accommodations)
  );
  const [bookingComplete, setBookingComplete] = useState(false);
  const [newBookingId, setNewBookingId] = useState(null);

  // Booking details
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);

  const [hotelPricing, setHotelPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
const [accommodationQuantity, setAccommodationQuantity] = useState(1);  

  // Validation state
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: ''
  });

  // Guest info
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });

  // ADD after existing states (after accommodationQuantity state):
const [selectedMealPlan, setSelectedMealPlan] = useState(mealPlan);

// ADD this helper — mirrors PropertyDetail logic:
const getMealPlanLabel = (plan) => {
  if (plan === 'CP')  return 'Incl. Breakfast';
  if (plan === 'MAP') return 'Incl. Breakfast & Dinner';
  if (plan === 'AP')  return 'Incl. All Meals';
  return 'Room Only';
};

// ADD this to get the correct base price based on meal plan:
const getSelectedRoomPrice = () => {
  if (!selectedRoom) return 0;
  if (selectedMealPlan === 'EP') return selectedRoom.pricePerNightB2C;
  return selectedRoom.mealPlanPricing?.[selectedMealPlan]?.B2C 
      || selectedRoom.pricePerNightB2C;
};

  const authUser = useSelector((state) => state.userauth?.user);

  
useEffect(() => {
  if (prefilledCheckIn) setCheckIn(prefilledCheckIn);
  if (prefilledCheckOut) setCheckOut(prefilledCheckOut);
  if (prefilledGuests) setGuests(prefilledGuests);
  if (selectedAccommodation) setSelectedRoom(selectedAccommodation);
  if (prefilledQuantity) setAccommodationQuantity(prefilledQuantity); 
   if (mealPlan) setSelectedMealPlan(mealPlan); 
}, [prefilledCheckIn, prefilledCheckOut, prefilledGuests, selectedAccommodation, prefilledQuantity]);

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const canGoNext = () => {
    switch(step) {
      case 1:
        return checkIn && checkOut && guests >= 1 && 
               new Date(checkOut) > new Date(checkIn);
      case 2:
        return guestInfo.firstName.trim() && 
       validateEmail(guestInfo.email) && 
       validatePhone(guestInfo.phone);
      default:
        return true;
    }
  };

  const isFormValid = () => {
    const phoneValid = validatePhone(guestInfo.phone);
    const emailValid = validateEmail(guestInfo.email);
    const requiredValid = guestInfo.firstName.trim();
    return phoneValid && emailValid && requiredValid;
  };

  // Shared price calculator (platform + extras, no GST)
  function calculateHotelTotalPrice(basePrice, hotelPricing, pricingLoading) {
    if (!hotelPricing || pricingLoading || typeof basePrice !== 'number') {
      return {
        totalPrice: basePrice || 0,
        platformAmount: 0,
        fixedFees: 0,
        breakdown: { basePrice: basePrice || 0 }
      };
    }

    const {
      platform_price = 0,
      miscellaneous_charges = 0,
      additional_charges_one = 0,
      additional_charges_two = 0,
    } = hotelPricing;

    const platformAmount = (basePrice * platform_price) / 100;
    const fixedFees = miscellaneous_charges + additional_charges_one + additional_charges_two;
    const totalFees = platformAmount + fixedFees;
    const totalPrice = basePrice + totalFees;

    return {
      totalPrice,
      platformAmount,
      fixedFees,
      breakdown: {
        basePrice,
        platformAmount,
        miscellaneous_charges,
        additional_charges_one,
        additional_charges_two,
      },
    };
  }

  // Fetch hotel pricing
  useEffect(() => {
    async function fetchHotelPricing() {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/get-hotelpricing`);
        setHotelPricing(res.data.data);
      } catch (err) {
        console.error('Failed to fetch hotel pricing:', err);
        setHotelPricing(null);
      } finally {
        setPricingLoading(false);
      }
    }
    fetchHotelPricing();
  }, []);

  // Price including platform + extras per night (no GST)
  const getRoomTotalPrice = (roomPrice) => {
    if (typeof roomPrice !== 'number') return 0;
    return calculateHotelTotalPrice(roomPrice, hotelPricing, pricingLoading).totalPrice;
  };

  // Nights count
  const getNights = () => {
    if (!checkIn || !checkOut) return 1;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = (end - start) / (1000 * 60 * 60 * 24);
    return diff > 0 ? diff : 1;
  };

    const nights = getNights();
 const baseWithFeesPerNight = getRoomTotalPrice(
  getSelectedRoomPrice()  
);
  const gstPercent = hotelPricing?.gst_price || 0;
  const gstRate = gstPercent / 100;

  const perNightWithGst =
    typeof baseWithFeesPerNight === 'number'
      ? baseWithFeesPerNight * (1 + gstRate)
      : 0;

 const baseForStay = typeof baseWithFeesPerNight === 'number'
  ? baseWithFeesPerNight * nights * accommodationQuantity 
  : 0;

  const gstForStay = typeof baseWithFeesPerNight === 'number'
  ? baseWithFeesPerNight * gstRate * nights * accommodationQuantity  
  : 0;

const totalBeforeDiscount = baseForStay + gstForStay;

const finalDiscount = Math.min(discount || 0, totalBeforeDiscount);

const grandTotalForStay = totalBeforeDiscount - finalDiscount;


const handleCashfreePayment = async () => {
  const roundedTotal = Math.round(grandTotalForStay);
  if (roundedTotal <= 0) { alert('Invalid total amount.'); return; }

  setPaymentLoading(true);
  try {
    // Step 1: Get userId
    const tokenString = localStorage.getItem('token');
    let userId = null;
    if (tokenString) {
      const decodedToken = jwtDecode(tokenString);
      userId = decodedToken?.user?.id || null;
    }

    // Step 2: Create booking first with status pending
    const bookingPayload = {
      propertyId: property._id,
      accommodationId: selectedRoom._id,
      accommodationQuantity,
      userId,
      checkIn, checkOut, guests,
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName || '',
      email: guestInfo.email,
      phone: guestInfo.phone,
      specialRequests: guestInfo.specialRequests,
      amount: roundedTotal,
      couponCode: coupon?.coupon_code || null,
      discountAmount: finalDiscount,
      currency: 'INR',
      mealPlan: selectedMealPlan,
      mealPlanPrice: getSelectedRoomPrice(),
      paymentGateway: 'cashfree',
      paymentStatus: 'pending',
      status: 'pending',
      eventId,
      fbc: getFbc(),              
  fbp: getMetaCookie("_fbp"), 
    };

    const bookingRes = await axios.post(
      `${KeyUri.BACKEND_URI}/hotelbookings`,
      bookingPayload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    const bookingId = bookingRes.data.data?._id || bookingRes.data._id;
    if (!bookingId) throw new Error('Booking creation failed');

    // Step 3: Create Cashfree order
    const rawToken = localStorage.getItem('tokenUser') ||
      localStorage.getItem('token');
    const token = rawToken?.replace(/^"|"$/g, '');

    const orderRes = await axios.post(
      `${KeyUri.BACKEND_URI}/hotel-bookings/create-cashfree-order`,
      {
        bookingId,
        amount: roundedTotal,
        firstName: guestInfo.firstName,
        lastName: guestInfo.lastName || '',
        email: guestInfo.email,
        phone: guestInfo.phone,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    const { payment_session_id, order_id } = orderRes.data;
    if (!payment_session_id) throw new Error('Failed to get payment session');

    // Step 4: Open Cashfree checkout
   if (!window.Cashfree) {
  alert('Cashfree SDK not loaded. Please refresh and try again.');
  setPaymentLoading(false);
  return;
}

const cashfree = window.Cashfree({
  mode: process.env.REACT_APP_CASHFREE_ENV === 'PRODUCTION' ? 'production' : 'sandbox',
});

sessionStorage.setItem('cashfree_hotel_booking_id', bookingId);
sessionStorage.setItem('cashfree_hotel_event_id', eventId || '');
sessionStorage.setItem('cashfree_hotel_value', String(roundedTotal));
sessionStorage.setItem('cashfree_hotel_property_id', property._id);
    cashfree.checkout({
      paymentSessionId: payment_session_id,
      redirectTarget: '_self',
    });

    // Step 5: Poll to verify payment
    const pollInterval = setInterval(async () => {
      try {
        const verifyRes = await axios.post(
          `${KeyUri.BACKEND_URI}/hotel-bookings/verify-cashfree-payment`,
          { orderId: order_id, bookingId, fbc: getFbc(), fbp: getMetaCookie("_fbp") }
        );

        if (verifyRes.data.success) {
          clearInterval(pollInterval);

          const confirmedBooking = verifyRes.data.booking;
          const confirmedBookingId = confirmedBooking?._id || bookingId;

          trackEvent("Purchase", {
    value: Math.round(grandTotalForStay),
    currency: "INR",
    content_ids: [property._id],
    content_type: "hotel_room",
    content_name: property.propertyName,
  }, eventId);

          // Track coupon if applied
          if (coupon && finalDiscount > 0) {
            try {
              await axios.post(
                `${KeyUri.BACKEND_URI}/hotel-coupon/apply`,
                {
                  coupon_code: (coupon.coupon_code || '').trim(),
                  bookingId: confirmedBookingId,
                  userId,
                  totalAmount: totalBeforeDiscount,
                  checkInDate: checkIn,
                  propertyId: property._id,
                  accommodationId: selectedRoom._id,
                  bookingRef: confirmedBooking?.bookingId || confirmedBookingId,
                  guests: Number(guests),
                  rooms: Number(accommodationQuantity),
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                }
              );
            } catch (couponErr) {
              console.error('Coupon tracking error:', couponErr);
            }
          }

          setNewBookingId(confirmedBookingId);
          setBookingComplete(true);
        }
      } catch (err) {
        // Still pending — keep polling
      }
    }, 3000);

    // Stop polling after 10 minutes
    setTimeout(() => clearInterval(pollInterval), 600000);

  } catch (err) {
    console.error('Cashfree hotel payment error:', err);
    alert(err?.response?.data?.message || 'Payment initiation failed. Please try again.');
  } finally {
    setPaymentLoading(false);
  }
};

// Replace handleRazorpayPayment with this:
const handleRazorpayPayment = async () => {
  const roundedTotal = Math.round(grandTotalForStay);

  if (roundedTotal <= 0) {
    alert('Invalid total amount.');
    return;
  }

  setPaymentLoading(true);

  try {
    // Step 1: Get userId
    const tokenString = localStorage.getItem('token');
    let userId = null;
    if (tokenString) {
      const decodedToken = jwtDecode(tokenString);
      userId = decodedToken?.user?.id || null;
    }

    // Step 2: Create booking first with status pending
    const bookingPayload = {
      propertyId: property._id,
      accommodationId: selectedRoom._id,
      accommodationQuantity,
      userId,
      checkIn,
      checkOut,
      guests,
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName || '',
      email: guestInfo.email,
      phone: guestInfo.phone,
      specialRequests: guestInfo.specialRequests,
      amount: roundedTotal,
      couponCode: coupon?.coupon_code || null,
      discountAmount: finalDiscount,
      currency: 'INR',
      mealPlan: selectedMealPlan,
      mealPlanPrice: getSelectedRoomPrice(),
      paymentGateway: 'razorpay',
      paymentStatus: 'pending',
      status: 'pending',
      eventId,
    };

    const bookingRes = await axios.post(
      `${KeyUri.BACKEND_URI}/hotelbookings`,
      bookingPayload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    const bookingId = bookingRes.data.data?._id || bookingRes.data._id;
    if (!bookingId) throw new Error('Booking creation failed');

    // Step 3: Create Razorpay order on backend (payment_capture: 1)
    const orderRes = await axios.post(
      `${KeyUri.BACKEND_URI}/hotel-bookings/razorpay/create-order`,
      { bookingId, amount: roundedTotal },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { orderId, amount, currency } = orderRes.data;

    // Step 4: Open Razorpay with order_id
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: amount,       // from order (in paise)
      currency: currency,   // from order
      order_id: orderId,    // ← enables payment_capture: 1 / auto-capture
      name: property.propertyName || 'Booking',
      description: selectedRoom?.name || 'Room Booking',
      image: property.images?.[0] || '',
      prefill: {
        name: `${guestInfo.firstName} ${guestInfo.lastName || ''}`.trim(),
        email: guestInfo.email,
        contact: guestInfo.phone,
      },
      notes: {
        property_id: property._id,
        room_name: selectedRoom?.name,
        bookingId: bookingId,
      },
      theme: { color: '#2563eb' },
      handler: async function (response) {
        // Step 5: Update payment status in DB
        try {
          await axios.put(
            `${KeyUri.BACKEND_URI}/hotelbookings/${bookingId}/payment`,
            {
              paymentStatus: 'captured',
              status: 'confirmed',
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              amountPaid: roundedTotal,
            },
            { headers: { 'Content-Type': 'application/json' } }
          );
        } catch (updateErr) {
          console.error('Payment status update failed:', updateErr);
        }

        // Step 6: Track coupon if applied
        if (coupon && finalDiscount > 0) {
          try {
            const token = localStorage.getItem('tokenUser')?.replace(/^"|"$/g, '') ||
              localStorage.getItem('token')?.replace(/^"|"$/g, '');

            await axios.post(
              `${KeyUri.BACKEND_URI}/hotel-coupon/apply`,
              {
                coupon_code: (coupon.coupon_code || '').trim(),
                bookingId,
                userId,
                totalAmount: totalBeforeDiscount,
                checkInDate: checkIn,
                propertyId: property._id,
                accommodationId: selectedRoom._id,
                bookingRef: bookingId,
                guests: Number(guests),
                rooms: Number(accommodationQuantity),
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              }
            );
          } catch (couponErr) {
            console.error('Coupon tracking error:', couponErr);
          }
        }

        // Step 7: Send confirmation (WhatsApp + Email + Vendor payout)
        try {
          await axios.post(`${KeyUri.BACKEND_URI}/hotel-confirm`, {
            booking_idd: bookingId,
          });
        } catch (confirmErr) {
          console.error('Confirmation send failed:', confirmErr.message);
        }

        trackEvent("Purchase", {
  value: Math.round(grandTotalForStay),
  currency: "INR",
  content_ids: [property._id],
  content_type: "hotel_room",
  content_name: property.propertyName,
}, eventId);

        setNewBookingId(bookingId);
        setBookingComplete(true);
      },
      modal: {
        ondismiss: async () => {
          try {
            await axios.put(
              `${KeyUri.BACKEND_URI}/hotelbookings/${bookingId}/payment`,
              { paymentStatus: 'failed', status: 'cancelled' }
            );
          } catch (e) {
            console.error(e);
          }
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

  } catch (err) {
    console.error('Razorpay hotel payment error:', err);
    alert(err?.response?.data?.message || 'Payment initiation failed. Please try again.');
  } finally {
    setPaymentLoading(false);
  }
};

const handleHdfcPayment = async () => {
  const roundedTotal = Math.round(grandTotalForStay);
  if (roundedTotal <= 0) { alert('Invalid total amount.'); return; }

  setPaymentLoading(true);
  try {
    const tokenString = localStorage.getItem('token');
    let userId = null;
    if (tokenString) {
      const decodedToken = jwtDecode(tokenString);
      userId = decodedToken?.user?.id || null;
    }

    const bookingPayload = {
      propertyId: property._id,
      accommodationId: selectedRoom._id,
      accommodationQuantity,
      userId,
      checkIn, checkOut, guests,
      firstName: guestInfo.firstName,
      lastName: guestInfo.lastName || '',
      email: guestInfo.email,
      phone: guestInfo.phone,
      specialRequests: guestInfo.specialRequests,
      amount: roundedTotal,
      couponCode: coupon?.coupon_code || null,
      discountAmount: finalDiscount,
      currency: 'INR',
      mealPlan: selectedMealPlan,
      mealPlanPrice: getSelectedRoomPrice(),
      paymentGateway: 'hdfc',
      paymentStatus: 'pending',
      status: 'pending',
      eventId,
      fbc: getFbc(),
      fbp: getMetaCookie("_fbp"),
    };

    const bookingRes = await axios.post(
      `${KeyUri.BACKEND_URI}/hotelbookings`,
      bookingPayload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    const bookingId = bookingRes.data.data?._id || bookingRes.data._id;
    if (!bookingId) throw new Error('Booking creation failed');

    const orderRes = await axios.post(
      `${KeyUri.BACKEND_URI}/payments/hdfc/create-order`,
      { type: "hotel", bookingId },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { payment_url } = orderRes.data;
    if (!payment_url) throw new Error('HDFC did not return a payment URL');

    sessionStorage.setItem('hdfc_hotel_booking_id', bookingId);
    window.location.href = payment_url; // leaves the React app
  } catch (err) {
    console.error('HDFC hotel payment error:', err);
    alert(err?.response?.data?.message || 'Payment initiation failed. Please try again.');
    setPaymentLoading(false);
  }
};

const handleBookingSubmit = async (e) => {
  e.preventDefault();
  if (paymentGateway === 'cashfree') {
    handleCashfreePayment();
  } else if (paymentGateway === 'hdfc') {
    await handleHdfcPayment();
  } else {
    await handleRazorpayPayment();
  }
};


  const handleViewInvoice = () => {
    if (newBookingId) {
      navigate(`/hotel-invoice?bookingId=${newBookingId}`);
      onClose();
    }
  };

  const maxGuests = selectedRoom?.maxOccupancy;

  if (bookingComplete) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          zIndex: 1300,
        }}
      >
        <Card sx={{ maxWidth: 450, p: 4, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              backgroundColor: '#d1fae5',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Check size={32} color="#059669" />
          </div>
          <Typography variant="h5" mb={2}>
            Booking Confirmed!
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Your booking request has been sent to the property. You will receive a
            confirmation email shortly.
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Button 
              fullWidth 
              variant="contained" 
              onClick={handleViewInvoice}
              sx={{ backgroundColor: '#059669' }}
            >
              View Invoice
            </Button>
            <Button fullWidth variant="outlined" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    );
  }

   if (!selectedRoom) {
    return (
      <div style={{ padding: 20 }}>
        <Typography color="error">No accommodations available to book.</Typography>
        <Button onClick={onClose}>Close</Button>
      </div>
    );
  }
  

return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
        zIndex: 1300,
      }}
    >
      <Card sx={{ maxWidth: 768, width: '100%', my: 4, mt: 24 }}>
        <div
          style={{
            borderBottom: '1px solid #e0e0e0',
            padding: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Complete Your Booking</Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <X size={20} />
          </IconButton>
        </div>

        <div style={{ padding: 16 }}>
          {/* Progress Steps */}
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4 }}
          >
            {[1, 2, 3].map((num, i) => (
              <React.Fragment key={num}>
                <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: step >= num ? '#2563eb' : '#e0e0e0',
                      color: step >= num ? '#fff' : '#9ca3af',
                      fontWeight: 'bold',
                    }}
                  >
                    {num}
                  </div>
                  <Typography sx={{ color: step >= num ? '#111827' : '#9ca3af' }}>
                    {num === 1 ? 'Details' : num === 2 ? 'Guest Info' : 'Payment'}
                  </Typography>
                </Grid>
                {i < 2 && (
                  <Grid item xs>
                    <Divider sx={{ borderColor: '#e0e0e0', mx: 2, height: 1 }} />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>

          <form onSubmit={handleBookingSubmit}>
      
{step === 1 && Object.values(errors).some(e => e) && (
  <div style={{ 
    background: '#fef2f2', border: '1px solid #fecaca', 
    borderRadius: 8, padding: 12, mb: 2 
  }}>
    <Typography color="error.main" fontWeight={500}>
      Please fix errors below
    </Typography>
  </div>
)}

            {/* Step 1: Booking Details */}
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <Typography variant="h6" mb={2}>
                    Select Accommodation
                  </Typography>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
         {property.accommodations.map((acc, index) => {
  const isActive = selectedRoom === acc;
  return (
    <div
      key={index}
      onClick={() => setSelectedRoom(acc)}
      style={{
        borderRadius: 8,
        border: isActive ? '2px solid #2563eb' : '1px solid #d1d5db',
        backgroundColor: isActive ? '#dbeafe' : 'transparent',
        padding: 16,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        userSelect: 'none',
        transition: 'border-color 0.3s, background-color 0.3s',
      }}
    >
                        <div>
                          <Typography fontWeight={500}>{acc?.name || 'Room'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {acc?.type}
                          </Typography>
                        </div>
               <Typography variant="h6">
  ₹{(() => {
   
    const basePrice =
      selectedMealPlan === 'EP'
        ? acc?.pricePerNightB2C
        : acc?.mealPlanPricing?.[selectedMealPlan]?.B2C ||
          acc?.pricePerNightB2C;

    const totalPrice = getRoomTotalPrice(basePrice);

    return typeof totalPrice === 'number'
      ? totalPrice.toLocaleString('en-IN')
      : 'N/A';
  })()}{" "}
  /night
</Typography>
                      </div>
                  );
})}
                  </div>
                </div>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor="checkin"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      Check-in Date
                    </InputLabel>
                    <div style={{ position: 'relative' }}>
                      <Calendar
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: '35%',
                          transform: 'translateY(-50%)',
                          color: '#9ca3af',
                        }}
                        size={16}
                      />
                     <TextField
  id="checkin"
  type="date"
  required
  error={!!errors.checkIn}
  helperText={errors.checkIn || "Select check-in date"}
  value={checkIn}
  onChange={(e) => {
    const value = e.target.value;
    setCheckIn(value);
    setErrors(prev => ({ ...prev, checkIn: '' }));
    
    // Validate date range
    if (checkOut && new Date(checkOut) <= new Date(value)) {
      setErrors(prev => ({ ...prev, checkOut: 'Check-out must be after check-in' }));
    }
  }}
  InputProps={{ style: { paddingLeft: 32 } }}
/>
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor="checkout"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      Check-out Date
                    </InputLabel>
                    <div style={{ position: 'relative' }}>
                      <Calendar
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: '35%',
                          transform: 'translateY(-50%)',
                          color: '#9ca3af',
                        }}
                        size={16}
                      />
                     <TextField
  id="checkout"
  type="date"
  required
  error={!!errors.checkOut}
  helperText={errors.checkOut || "Select check-out date"}
  value={checkOut}
  onChange={(e) => {
    const value = e.target.value;
    setCheckOut(value);
    setErrors(prev => ({ ...prev, checkOut: '' }));
    
    if (checkIn && new Date(value) <= new Date(checkIn)) {
      setErrors(prev => ({ ...prev, checkIn: 'Check-in must be before check-out' }));
    }
  }}
  InputProps={{ style: { paddingLeft: 32 } }}
/>
                    </div>
                  </Grid>
                </Grid>

          <div>
  <InputLabel
    htmlFor="guests"
    style={{ display: 'block', marginBottom: 4 }}
  >
    Number of Guests
  </InputLabel>

  <div
    style={{
      maxWidth: 130,
      border: '1px solid #d1d5db',
      borderRadius: 4,
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      height: 40,
    }}
  >
    <IconButton
      size="small"
      onClick={() => {
        const value = Math.max(1, guests - 1);
        setGuests(value);
        setErrors(prev => ({ ...prev, guests: '' }));
      }}
      disabled={guests <= 1}
      sx={{
        width: 40,
        height: '100%',
        borderRadius: 0,
      }}
    >
      <Typography fontWeight={700}>−</Typography>
    </IconButton>

    <Typography
      sx={{
        flex: 1,
        textAlign: 'center',
        fontWeight: 600,
        borderLeft: '1px solid #e5e7eb',
        borderRight: '1px solid #e5e7eb',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {guests}
    </Typography>

    <IconButton
      size="small"
      onClick={() => {
        const value = Math.min(maxGuests, guests + 1);
        setGuests(value);
        setErrors(prev => ({ ...prev, guests: '' }));
      }}
      disabled={guests >= maxGuests}
      sx={{
        width: 40,
        height: '100%',
        borderRadius: 0,
      }}
    >
      <Typography fontWeight={700}>+</Typography>
    </IconButton>
  </div>

  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ mt: 0.5, display: 'block' }}
  >
    Maximum {maxGuests} guest{maxGuests > 1 ? "s" : ""}
  </Typography>

  {errors.guests && (
    <Typography
      variant="caption"
      color="error"
      sx={{ display: 'block', mt: 0.5 }}
    >
      {errors.guests}
    </Typography>
  )}
</div>

               <Button 
  variant="contained" 
  fullWidth 
  disabled={!canGoNext()}
  onClick={() => setStep(2)}
>
  {canGoNext() ? 'Continue' : 'Complete booking details'}
</Button>
              </div>
            )}

            {/* Step 2: Guest Information */}
            {step === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <Typography variant="h6" mb={1}>
                  Guest Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor="firstName"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      First Name
                    </InputLabel>
                  <TextField
  id="firstName"
  variant="outlined"
  size="small"
  fullWidth
  required
  error={!!errors.firstName}
  helperText={errors.firstName || "Enter first name"}
  value={guestInfo.firstName}
  onChange={(e) => {
    const value = e.target.value;
    setGuestInfo({ ...guestInfo, firstName: value });
    if (value.trim()) setErrors({ ...errors, firstName: '' });
  }}
/>
                  </Grid>
                  <Grid item xs={6}>
                    <InputLabel
                      htmlFor="lastName"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      Last Name
                    </InputLabel>
         <TextField
  id="lastName"
  variant="outlined"
  size="small"
  fullWidth
  error={!!errors.lastName}
  helperText={errors.lastName || "Optional"}
  value={guestInfo.lastName}
  onChange={(e) => {
    const value = e.target.value;
    setGuestInfo({ ...guestInfo, lastName: value });
    setErrors({ ...errors, lastName: '' });
  }}
/>
                  </Grid>
                </Grid>

                <div>
                  <InputLabel
                    htmlFor="email"
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Email
                  </InputLabel>
                <TextField
  id="email"
  type="email"
  variant="outlined"
  size="small"
  fullWidth
  required
  error={!!errors.email}
  helperText={errors.email || "Enter valid email"}
  value={guestInfo.email}
  onChange={(e) => {
    const value = e.target.value;
    setGuestInfo({ ...guestInfo, email: value });
    if (validateEmail(value)) {
      setErrors({ ...errors, email: '' });
    }
  }}
/>
                </div>

                <div>
                  <InputLabel
                    htmlFor="phone"
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Phone Number
                  </InputLabel>
                <TextField
  id="phone"
  type="tel"
  variant="outlined"
  size="small"
  fullWidth
  required
  error={!!errors.phone}
  helperText={errors.phone || "10-digit mobile (eg: 9876543210)"}
  value={guestInfo.phone}
  onChange={(e) => {
    
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setGuestInfo({ ...guestInfo, phone: value });
    if (validatePhone(value)) {
      setErrors({ ...errors, phone: '' });
    } else if (value.length === 10) {
      setErrors({ ...errors, phone: '' });
    }
  }}
/>
                </div>

                <div>
                  <InputLabel
                    htmlFor="specialRequests"
                    style={{ display: 'block', marginBottom: 4 }}
                  >
                    Special Requests (Optional)
                  </InputLabel>
                  <TextField
                    id="specialRequests"
                    multiline
                    rows={3}
                    variant="outlined"
                    fullWidth
                    placeholder="Any special requests or requirements..."
                    value={guestInfo.specialRequests}
                    onChange={(e) =>
                      setGuestInfo({ ...guestInfo, specialRequests: e.target.value })
                    }
                  />
                </div>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button variant="outlined" fullWidth onClick={() => setStep(1)}>
                      Back
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                  <Button 
  variant="contained" 
  fullWidth 
  disabled={!canGoNext()}
  onClick={() => setStep(3)}
>
  {canGoNext() ? 'Continue to Payment' : 'Fix guest information'}
</Button>
                  </Grid>
                </Grid>
              </div>
            )}

            {/* Step 3: Payment */}
     {step === 3 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 24,
                  marginTop: 24
                }}
              >
                <Typography variant="h6" mb={1}>
                  Payment Details
                </Typography>

                <Card
                  variant="outlined"
                  sx={{ backgroundColor: '#f9fafb', p: 2 }}
                >
                  <Typography variant="subtitle1" mb={1}>
                    Booking Summary
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: '#4b5563'
                      }}
                    >
                      <span>Property</span>
                      <span>{property.propertyName || ''}</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: '#4b5563'
                      }}
                    >
                      <span>Accommodation</span>
                      <span>{selectedRoom?.name || ''}</span>
                    </div>
                   
<div style={{
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 14,
  color: '#4b5563'
}}>
  <span>Selected Plan</span>
  <span>{getMealPlanLabel(selectedMealPlan)}</span>
</div>
                    {/* Price per night including fees */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: '#4b5563'
                      }}
                    >
                      <span>Price per night</span>
                      <span>
                        ₹
                        {typeof baseWithFeesPerNight === 'number'
                          ? baseWithFeesPerNight.toLocaleString('en-IN')
                          : 'N/A'}
                      </span>
                    </div>

                    <Divider sx={{ my: 1 }} />

                    {/* Base × nights */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: '#4b5563'
                      }}
                    >
                     <span>{accommodationQuantity} room{accommodationQuantity > 1 ? 's' : ''} × {nights} night(s)</span>
<span>₹{Math.round(baseForStay).toLocaleString('en-IN')}</span>

                    </div>

                    {/* GST for whole stay */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 14,
                        color: '#4b5563'
                      }}
                    >
                      <span>GST ({gstPercent}%)</span>
                      <span>
                        ₹
                        {Math.round(gstForStay).toLocaleString('en-IN')}
                      </span>
                    </div>

{coupon && finalDiscount > 0 && (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    color: '#059669',
    fontSize: 14
  }}>
    <span>Coupon ({coupon.coupon_code})</span>
    <span>-₹{Math.round(finalDiscount).toLocaleString('en-IN')}</span>
  </div>
)}
                    {/* GRAND TOTAL for whole stay – matches Razorpay */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 'bold'
                      }}
                    >
                      <span>Total Amount</span>
                      <span style={{ fontSize: 20 }}>
                        ₹
                        {Math.round(
                          grandTotalForStay
                        ).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </Card>
{/* Gateway Selector */}
<div style={{ marginBottom: 16 }}>
  <Typography variant="subtitle2" fontWeight={600} mb={1}>
    Choose Payment Method
  </Typography>
    <Grid container spacing={2}>
    {[
      { key: 'cashfree', label: 'Cashfree', sub: 'UPI · Cards · Wallets' },
      { key: 'razorpay', label: 'Razorpay', sub: 'UPI · Cards · Net Banking' },
    /*  { key: 'hdfc', label: 'HDFC Bank', sub: 'UPI · Cards · Net Banking' },*/
    ].map(({ key, label, sub }) => (
      <Grid item xs={4} key={key}>
        <div
          onClick={() => setPaymentGateway(key)}
          style={{
            border: `2px solid ${paymentGateway === key ? '#2563eb' : '#e5e7eb'}`,
            borderRadius: 8,
            padding: '12px 8px',
            cursor: 'pointer',
            backgroundColor: paymentGateway === key ? '#eff6ff' : 'white',
            textAlign: 'center',
          }}
        >
          <Typography fontWeight={600} fontSize={14}
            color={paymentGateway === key ? '#2563eb' : 'text.primary'}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">{sub}</Typography>
          {paymentGateway === key && (
            <Typography variant="caption" display="block"
              sx={{ color: '#2563eb', fontWeight: 600 }}>✓ Selected</Typography>
          )}
        </div>
      </Grid>
    ))}
  </Grid>
</div>

<Grid container spacing={2}>
  <Grid item xs={4}>
    <Button variant="outlined" fullWidth onClick={() => setStep(2)}>Back</Button>
  </Grid>
  <Grid item xs={8}>
    <Button
      variant="contained"
      fullWidth
      disabled={!isFormValid() || paymentLoading}
      onClick={handleBookingSubmit}
      sx={{
        backgroundColor: '#2563eb',
        '&:hover': { backgroundColor: '#1d4ed8' },
        py: 1.5,
        fontWeight: 700,
        textTransform: 'none',
      }}
    >
         {paymentLoading
        ? 'Processing...'
        : !isFormValid()
        ? 'Complete Guest Info First'
        : `Pay ₹${Math.round(grandTotalForStay).toLocaleString('en-IN')} via ${
            paymentGateway === 'cashfree' ? 'Cashfree' : paymentGateway === 'hdfc' ? 'HDFC Bank' : 'Razorpay'
          }`}
    </Button>
  </Grid>
</Grid>
              </div>
            )}
          </form>
        </div>
      </Card>
    </div>
  );
}